import React, { useRef } from 'react';

import HCaptcha from '@hcaptcha/react-hcaptcha';
import { getTranslate } from 'react-localize-redux';

import { getUserMessage } from '../../../../core/api/apiUtils';
import { postRequestPhoneLoginCodeSms } from '../../../../core/api/user/user.service';
import { analyticsService } from '../../../../core/services/analyticsService';
import { logger } from '../../../../core/services/loggerService';
import { AxiosError } from '../../../../core/types/data.types';
import { useCountdown } from '../../../../utils/hooks/useCountdown';
import { Button } from '../../../atoms/Button';

type Props = {
  onBackLink?(message?: string): void;
  onError?(error: Error): void;
  onSubmit?(): void;
  onSuccess?(): void;
  phoneNumber: string;
  translate: ReturnType<typeof getTranslate>;
};

export function PhoneCallButton(props: Props): JSX.Element {
  const { onBackLink, onError, onSubmit, onSuccess, phoneNumber, translate } =
    props;

  const hCaptchaRef = useRef<HCaptcha>(null);
  const isHCaptchaEnabled = !!process.env.REACT_APP_HCAPTCHA_SITE_KEY;
  const isCallRequested = useRef(false);
  const isSubmitting = useRef(false);
  const [countdown, startCountdownAgain] = useCountdown(30);

  const sendPhoneCodeCall = (hCaptchaToken?: string): void => {
    isSubmitting.current = true;
    isCallRequested.current = true;

    if (onSubmit) {
      onSubmit();
    }

    postRequestPhoneLoginCodeSms(
      { fallback: 'PhoneCall', phoneNumber },
      hCaptchaToken,
    )
      .then(() => {
        analyticsService.track({
          amp_label: 'Phone call requested',
          amp_phoneCallRequestedSuccessfully: true,
        });

        startCountdownAgain();

        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error: AxiosError) => {
        const message = getUserMessage(error, translate);

        analyticsService.track({
          amp_label: 'Phone call requested',
          amp_phoneCallRequestedSuccessfully: false,
        });
        logger.error(
          'API Request Error - PhoneCallButton RequestPhoneLoginCodeSms',
          {
            ...error,
            message,
            hCaptchaToken,
          },
        );

        if (onError) {
          onError(error);
        }
        onBackLink(message);
      })
      .finally(() => {
        isCallRequested.current = false;
      });
  };

  const handleRequestPhoneCall = async (): Promise<void> => {
    if (isHCaptchaEnabled) {
      await hCaptchaRef.current.execute({ async: true });
    } else {
      sendPhoneCodeCall();
    }
  };

  const getRequestPhoneCallButtonText = (): string => {
    const callStatusMessage = isCallRequested.current
      ? 'LoginConfirm_LoginConfirmDialog_RequestPhonecallAgain'
      : 'LoginConfirm_LoginConfirmDialog_RequestPhonecall';

    if (countdown > 0) {
      return `${translate(callStatusMessage)} ${countdown}s`;
    }

    return `${translate('Request_code_via_phone_call')}`;
  };

  return (
    <>
      {isHCaptchaEnabled && (
        <HCaptcha
          onVerify={sendPhoneCodeCall}
          ref={hCaptchaRef}
          sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
          size="invisible"
        />
      )}

      <Button
        data-fd="request-call"
        disabled={countdown > 0 || isSubmitting.current}
        onClick={handleRequestPhoneCall}
        size="small"
        variant="secondary"
      >
        {getRequestPhoneCallButtonText()}
      </Button>
    </>
  );
}
